import { Locale } from '@module/mdrt-org/shared/utils/enums/locale';
import { getIndefiniteArticle } from '@shared/utils/helpers/get-indefinite-article';

// eslint-disable-next-line import/no-unassigned-import
import './replace-template-variables.scss';

// Matches "a"/"an" immediately preceding a `{{Variable}}` token, e.g. "a {{MembershipYears}}".
const ARTICLE_BEFORE_VARIABLE_PATTERN = /\b(a|an)(\s+)\{\{\s*(\w+)\s*\}\}/giu;

// CMS authors write the article as plain "a"/"an" text (it's usually correct); this corrects
// it in place based on the referenced variable's actual value, so no CMS edit is required.
// "a"/"an" selection by leading vowel sound is an English-specific rule with no equivalent in
// the other locales this app supports, so this only runs for English.
const fixArticlesBeforeNumbers = (
  html: string,
  variables: Record<string, number | string | undefined>,
  locale: string
): string => {
  if (locale !== Locale.En) return html;

  return html.replaceAll(ARTICLE_BEFORE_VARIABLE_PATTERN, (match, article, whitespace, key) => {
    const rawValue = variables[key];
    if (rawValue === undefined) return match;

    const numericValue = Number(rawValue);
    if (Number.isNaN(numericValue)) return match;

    const correctArticle = getIndefiniteArticle(numericValue);
    const cased =
      article[0] === article[0].toUpperCase()
        ? correctArticle[0].toUpperCase() + correctArticle.slice(1)
        : correctArticle;

    return `${cased}${whitespace}{{${key}}}`;
  });
};

export const replaceTemplateVariables = (
  html: string,
  variables: Record<string, number | string | undefined>,
  activeField: null | string,
  staticFields: string[] = [],
  isPrintable?: boolean,
  locale: string = Locale.En
): string => {
  const replacer = (currentHtml: string) => (match: string, key: string, offset: number) => {
    const lookbackStart = Math.max(0, offset - 1000);
    const before = currentHtml.slice(lookbackStart, offset);
    const isInImgSource = /<img[^>]*src\s*=\s*["'][^"']*$/iu.test(before);

    if (isInImgSource) {
      return key in variables && variables[key] !== undefined ? String(variables[key]) : match;
    }

    if (variables[key] !== undefined) {
      if (staticFields.includes(key)) {
        return String(variables[key]);
      }

      const value = String(variables[key]);
      const classes = [
        'pdf-preview',
        isPrintable ? 'pdf-preview--printable' : '',
        activeField === key ? 'pdf-preview--active' : '',
      ]
        .filter(Boolean)
        .join(' ');

      return `<span class="${classes}" data-var="${key}">${value}</span>`;
    }

    return match;
  };

  let previous: string;
  let result = html;
  do {
    // Runs each iteration, not just once, so it also catches "a {{MembershipYears}}" text that
    // only becomes visible after an earlier substitution (e.g. ContentArea1Placeholder's CMS
    // rich text gets swapped in, revealing the MembershipYears token nested inside it).
    result = fixArticlesBeforeNumbers(result, variables, locale);
    previous = result;
    result = result.replaceAll(/\{\{\s*(\w+)\s*\}\}/gu, replacer(previous));
  } while (result !== previous);

  if (isPrintable) {
    result = result.replaceAll(
      /<p([^>]*)>(.*?)<\/p>/gsu,
      (_match, attributes: string, content: string) => {
        const trimmed = content.replaceAll(/[\u00A0\u202F]/gu, '').trim();

        if (trimmed.length === 0) {
          return `<p${attributes}>${content}</p>`;
        }

        const cleaned = content.replaceAll(/[\u00A0\u202F]/gu, ' ');

        return `<p${attributes}>${cleaned}</p>`;
      }
    );
  }

  return result;
};
